import { CircleX, Edit, RefreshCw, Save, Trash2 } from "lucide-react";
import {
  Form,
  useDelete,
  useGetIdentity,
  useNotify,
  useResourceContext,
  useTranslate,
  useUpdate,
  usePermissions,
} from "ra-core";
import { useEffect, useRef, useState } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { Markdown } from "../misc/Markdown";
import { RelativeDate } from "../misc/RelativeDate";
import { Status } from "../misc/Status";
import type { ContactNote, DealNote, LoadNote } from "../types";
import { NoteAttachments } from "./NoteAttachments";
import { NoteInputs } from "./NoteInputs";
import { useGetSalesName } from "../sales/useGetSalesName";
import { DOCUMENT_TYPE_CHOICES } from "../loads/LoadInputs";

export const Note = ({
  showStatus,
  note,
  reference,
}: {
  showStatus?: boolean;
  note: DealNote | ContactNote | LoadNote;
  isLast: boolean;
  reference?: "contacts" | "companies" | "deals" | "loads" | "leads";
}) => {
  const [isHover, setHover] = useState(false);
  const [isEditing, setEditing] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const [isTruncated, setTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const resource = useResourceContext();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const isCurrentUser = note.sales_id === identity?.id;
  const isAdmin = permissions === "admin";
  const isDeleted = note.deleted === true;
  const salesName = useGetSalesName(note.sales_id, {
    enabled: !isCurrentUser,
  });

  // Detect if content is truncated
  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      setTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [note.text]);

  const [update, { isPending }] = useUpdate();

  const [deleteNote] = useDelete(resource, undefined, {
    mutationMode: "undoable",
    onSuccess: () => {
      notify("resources.notes.deleted", {
        type: "info",
        undoable: true,
        messageArgs: {
          _: "Note deleted",
        },
      });
    },
  });

  const handleDelete = () => {
    deleteNote(resource, { id: note.id, previousData: note });
  };

  const handleRecover = () => {
    update(
      resource,
      { id: note.id, data: { deleted: false }, previousData: note },
      {
        onSuccess: () => {
          notify("resources.notes.recovered", {
            type: "info",
            messageArgs: {
              _: "Note recovered",
            },
          });
        },
      },
    );
  };

  const handleEnterEditMode = () => {
    setEditing(!isEditing);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setHover(false);
  };

  const handleNoteUpdate: SubmitHandler<FieldValues> = (values) => {
    update(
      resource,
      { id: note.id, data: values, previousData: note },
      {
        onSuccess: () => {
          setEditing(false);
          setHover(false);
        },
      },
    );
  };

  const content = (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn("mb-4", isDeleted && "opacity-60")}
    >
      <div className="flex items-center space-x-4 w-full">
        {"company_id" in note && note.company_id ? (
          <ReferenceField source="company_id" reference="companies" link="show">
            <CompanyAvatar width={20} height={20} />
          </ReferenceField>
        ) : null}
        <div className="inline-flex h-full items-center text-sm text-muted-foreground">
          {translate(
            isCurrentUser
              ? "resources.notes.you_added"
              : "resources.notes.author_added",
            { name: salesName },
          )}{" "}
          {showStatus && note.status && (
            <Status className="ml-2" status={note.status} />
          )}
          {isDeleted && (
            <span className="ml-2 text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
              {translate("resources.notes.deleted_label", { _: "DELETED" })}
            </span>
          )}
        </div>
        <span className={`${isHover ? "visible" : "invisible"}`}>
          {!isDeleted && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEnterEditMode}
                    className="p-1 h-auto cursor-pointer"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{translate("resources.notes.action.edit")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isDeleted && isAdmin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRecover}
                    className="p-1 h-auto cursor-pointer text-green-600 hover:text-green-700"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{translate("resources.notes.action.recover")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {!isDeleted && isAdmin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDelete}
                    className="p-1 h-auto cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{translate("resources.notes.action.delete")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
        <div className="flex-1"></div>
        <span className="text-sm text-muted-foreground">
          <RelativeDate date={note.date} />
        </span>
      </div>
      {isEditing ? (
        <Form onSubmit={handleNoteUpdate} record={note} className="mt-1">
          <NoteInputs showStatus={showStatus} reference={reference} />
          <div className="flex justify-end mt-2 space-x-4">
            <Button
              variant="ghost"
              onClick={handleCancelEdit}
              type="button"
              className="cursor-pointer"
            >
              <CircleX className="w-4 h-4" />
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              {translate("resources.notes.action.update")}
            </Button>
          </div>
        </Form>
      ) : (
        <div className="pt-2 text-sm max-w-150">
          {/* Display subject and document type for load notes */}
          {"load_id" in note && (note.subject || note.document_type) && (
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              {note.subject && (
                <span className="font-medium">{note.subject}</span>
              )}
              {note.document_type && (
                <span className="text-xs bg-muted px-2 py-1 rounded">
                  {DOCUMENT_TYPE_CHOICES.find(c => c.id === note.document_type)?.name || note.document_type}
                </span>
              )}
            </div>
          )}
          {note.text && (
            <div
              ref={contentRef}
              className={cn(
                "overflow-hidden transition-[max-height] duration-300 ease-in-out",
                isExpanded ? "max-h-[5000px]" : "max-h-46",
              )}
            >
              <Markdown>{note.text}</Markdown>
            </div>
          )}
          {isTruncated && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!isExpanded);
              }}
              className="text-primary text-sm mt-1 underline hover:no-underline cursor-pointer"
            >
              {isExpanded
                ? translate("crm.common.show_less")
                : translate("crm.common.read_more")}
            </button>
          )}

          {note.attachments && <NoteAttachments note={note} />}
        </div>
      )}
    </div>
  );

  return content;
};
